import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { all, task, restartableTask } from "ember-concurrency";
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { readOnly } from '@ember/object/computed';
import { showModal } from 'sh-www/utils/modal-controls';
import { action } from '@ember/object';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { USER } from 'sh-www/models/user';
import { COMPANY_PROFILE__COMPANY_INCLUDES, COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN } from 'sh-www/constants/routes/company-profile';

const INVITED_SELLERS_PAGE_SIZE = 15;
const USERS_NO_PARAM_INCLUDES = [
  'location',
  'last-location',
  'profile',
  'services',
  'applications.thread',
  'applications.thread.messages'
];

class MyNetworkController extends Controller
{
  @service('state-managers.application') applicationStateManager;
  @service('commands.company-commands') companyCommands;
  @service('commands.cost-code-commands') costCodeCommands;
  @service('commands/story-code-commands') storyCodeCommands;
  @service('commands.user-commands') userCommands;
  @service('error-tracker') errorTracker;
  @service router;
  @service shData;
  @service session;
  @service store;
  @service media;

  @readOnly('session.currentUser') currentUser;
  @readOnly('session.currentUser.company.id') companyId;

  myNetworkResultsList = [];
  companyStatistics = null;
  allCostCodes = [];
  allStoryCodes = [];
  allServices = [];
  codesDisabled = false;

  pendingInvitesCount = null;

  userName = '';
  selectedServices = [];
  hasActiveProject = false;
  extendedNetwork = false;
  storyCode = null;
  costCode = null;
  locationLabel = '';
  locationLatLng = '';
  selectedFreelancer = null;

  @tracked searchParams = {};
  pagination = {};

  @task
  *loadSearchResultsPageTask(companyId, params, pageToLoad) {
    try {
      const parameterizedIncludes = [
        `company-seller-connections:company_id(${this.companyId}):in_network_only`,
        `service-company-user-scores:company_id(${this.companyId})`,
        `applications:company_id(${this.companyId}):active_applications_only`
      ];

      const response = yield this.userCommands.getNetworkUsers(
        companyId,
        pageToLoad,
        12,
        { hasSlug: true, approval: true, ...params },
        [...USERS_NO_PARAM_INCLUDES, ...parameterizedIncludes]
      );

      this.shData.pushResponseToStore(response);
      const myNetworkResultsList = this.shData.getModelArrayFromResponse(response, 'user');
      const pagination = this.shData.getPaginationModelFromResponse(response);

      this.setProperties({
        myNetworkResultsList,
        pagination,
      });
    } catch(error) {
      this.errorTracker.error('loadSearchResultsPageTask - failed to load My Network search results', this, error);
      throw error;
    }
  }

  @task
  *loadCompanyStatisticsTask(companyId){
    try {
      const response = yield this.companyCommands.loadCompany(companyId, ['statistics']);
      this.set('companyStatistics', {
        totalInNetworkCreators: response.statistics?.totalInNetworkCreators ?? 0,
        inNetworkProjects: response.statistics?.inNetworkProjects ?? 0,
        inNetworkServices: response.statistics?.inNetworkServices ?? 0
      });
    } catch(error) {
      handleErrorResponseWithModal(this, error, 'There was an error loading your network statistics. Please reload the page and try again.');
    }
  }

  @task
  *loadCompanyTask() {
    try {
      const response = yield this.companyCommands.loadCompany(this.companyId);
      this.set('companyStatistics', { ...this.companyStatistics, totalInNetworkCreators: response.networkSize });
    } catch (error) {
      this.errorTracker.error('loadCompanyTask - failed to reload company', this, error);
    }
  }

  @task
  *loadInvitedSellersTask(companyId, pageToLoad) {
    try {
      const response = yield this.companyCommands.loadInvites(
        companyId,
        pageToLoad,
        INVITED_SELLERS_PAGE_SIZE,
        { accepted: false, userType: USER.TYPES.SELLER }
      );
      this.set('pendingInvitesCount', response.pagination.total);
      return response;
    } catch(error) {
      this.errorTracker.error('loadInvitedSellersTask - failed to load pending invites results', this, error);
      throw error;
    }
  }

  @task
  *removeInvitedUserTask(invite) {
    try {
      yield this.companyCommands.cancelInvite(invite);
      this.set('pendingInvitesCount', this.pendingInvitesCount - 1);
      this.loadCompanyTask.perform();
    } catch (error) {
      this.errorTracker.error('removeInvitedUserTask - failed to remove invite', this, error);
      throw error;
    }
  }

  @task
  *resendInviteTask(invite) {
    try {
      return this.companyCommands.resendInvite(invite);
    } catch (error) {
      this.errorTracker.error('resendInviteTask - failed to resend invite', this, error);
      throw error;
    }
  }

  @task
  *loadCostCodesTask(company) {
    const costCodes = yield this.costCodeCommands.getCostCodesForCompany(company);
    this.set('allCostCodes', costCodes);
  }

  @task
  *loadStoryCodesTask(company) {
    const storyCodes = yield this.storyCodeCommands.getStoryCodesForCompany(company);
    this.set('allStoryCodes', storyCodes);
  }

  @task
  *loadServicesTask() {
    const services = yield this.store.findAll('service');
    this.set('allServices', services);
  }

  @restartableTask
  *loadSidebar(company, user) {
    this.set('selectedFreelancer', user);

    const companyIncludes = this.session.currentUser.isAdmin
      ? COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN
      : COMPANY_PROFILE__COMPANY_INCLUDES;

    const companyPromise = this.companyCommands.loadCompany(company.id, companyIncludes);

    const userPromise = this.userCommands.getUser(user.id, [
      `service-company-user-scores:company_id(${company.id})`,
      'service-company-user-scores.service',
      `company-seller-connections:in_network_or_extended(${company.id}).company`,
      'last-location',
      'location',
      'profile.languages',
      'profile.equipment',
      'services',
    ]);

    const [u, c] = yield all([userPromise, companyPromise]);

    return {
      user: u,
      company: c,
    };
  }

  @computed('session.currentUser.company.slug', 'selectedFreelancer.slug')
  get targetUrl() {
    return this.router.urlFor(EMBER_ROUTE_NAMES.COMPANY_FREELANCER, this.session.currentUser.company.slug, this.selectedFreelancer.slug);
  }

  @computed('pendingInvitesCount', 'media.isMediumAndDown')
  get viewPendingInviteText() {
    if (this.media.isMediumAndDown || !this.pendingInvitesCount) {
      return 'View pending';
    } else {
      return `View pending (${this.pendingInvitesCount})`;
    }
  }

  @computed('userName', 'locationLatLng', 'selectedServices.[]', 'storyCode', 'costCode', 'hasActiveProject', 'extendedNetwork')
  get areFormFieldsEmpty() {
    return Boolean(
      !this.userName
      && !this.locationLatLng.length
      && !this.selectedServices.length
      && !this.storyCode
      && !this.costCode
      && !this.hasActiveProject
      && !this.extendedNetwork
    );
  }

  @computed('areFormFieldsEmpty', 'loadSearchResultsPageTask.isRunning')
  get isClearButtonDisabled() {
    return this.loadSearchResultsPageTask.isRunning || this.areFormFieldsEmpty;
  }

  @computed('loadSearchResultsPageTask.isRunning', 'searchParams', 'areFormFieldsEmpty')
  get isSearchButtonDisabled() {
    return Boolean(!Object.keys(this.searchParams).length && this.areFormFieldsEmpty) || this.loadSearchResultsPageTask.isRunning;
  }

  @computed('locationLatLng', 'locationLabel')
  get locationValue() {
    return this.locationLatLng.length ? this.locationLabel : '';
  }

  @computed('allStoryCodes.@each.{name}')
  get storyCodeOptions() {
    const storyCodes = this.allStoryCodes.map(({ name }) => { return { name, value: name }; });
    return [{name: 'Any', value: ''}, ...storyCodes];
  }

  @computed('allCostCodes.@each.{name}')
  get costCodeOptions() {
    const costCodes = this.allCostCodes.map(({ name }) => { return { name, value: name }; });
    return [{name: 'Any', value: ''}, ...costCodes];
  }

  @computed('selectedServices.[]', 'selectedServices.@each.id')
  get selectedServiceIds() {
    return this.selectedServices.length ? this.selectedServices.map(({ id }) => id) : [];
  }

  @computed('session.currentUser.company.sharesRates')
  get showExtendedNetwork() {
    return this.session.currentUser.company.sharesRates;
  }

  buildSearchParams() {
    const params = {
      ...( this.userName ? { name: this.userName } : {}),
      ...( this.locationLatLng ? { location: this.locationLatLng } : {}),
      ...( this.selectedServiceIds.length ? { hasServices: this.selectedServiceIds } : {}),
      ...( this.costCode ? { costCode: this.costCode } : {}),
      ...( this.storyCode ? { storyCode: this.storyCode } : {}),
      ...( this.hasActiveProject ? { activeApplications: this.companyId } : {}),
      ...( this.extendedNetwork ? { extendedNetwork: this.companyId } : {}),
    };

    return params;
  }

  resetSearchParams() {
    this.setProperties({
      userName: '',
      locationLabel: '',
      locationLatLng: '',
      selectedFreelancer: null,
      selectedServices: [],
      hasActiveProject: false,
      extendedNetwork: false,
      storyCode: null,
      costCode: null,
      codesDisabled: false,
    });
  }

  @action
  handleBlurbButtonClicked() {
    showModal(this, 'invite-creators', {
      onInviteSucceeded: this.onInviteSucceeded,
      onInviteRequestSucceeded: () => this.applicationStateManager.addToast('Request submitted to your account manager.'),
      networkSize: this.companyStatistics?.totalInNetworkCreators ?? 0,
    });
  }

  @action
  onInviteSucceeded() {
    this.applicationStateManager.addToast('1 invite sent');
    this.loadCompanyTask.perform();
    this.set('pendingInvitesCount', this.pendingInvitesCount + 1);
  }

  @action
  handleOnBlurbLinkClicked() {
    showModal(this, 'pending-invites-modal', {
      loadInvitedSellersTask: this.loadInvitedSellersTask,
      removeInvitedUserTask: this.removeInvitedUserTask,
      resendInviteTask: this.resendInviteTask
    });
  }

  @action
  handlePreviousPageClicked() {
    window.scrollTo({ top: 0 });
    this.loadSearchResultsPageTask.perform(this.companyId, this.searchParams, this.pagination.currentPage - 1);
  }

  @action
  handleNextPageClicked() {
    window.scrollTo({ top: 0 });
    this.loadSearchResultsPageTask.perform(this.companyId, this.searchParams, this.pagination.currentPage + 1);
  }

  @action
  handleNameChange(value) {
    this.set('userName', value)
  }

  @action
  handleLocationChange(location) {
    this.setProperties({
      locationLatLng: location ? `${location.geometry.location.lat()},${location.geometry.location.lng()}` : '',
      locationLabel: location ? location.formatted_address : ''
    });
  }

  @action
  handleServiceSelected(serviceOption) {
    const selectedServices = this.selectedServices;
    if (!selectedServices.includes(service.id)) {
      this.set('selectedServices', [...selectedServices, serviceOption]);
    }
  }

  @action
  handleServiceRemoved(serviceOption) {
    const selectedServices = this.selectedServices;
    this.set('selectedServices', selectedServices.filter(({ id }) => id !== serviceOption.id));
  }

  @action
  handleHasActiveProjectClicked() {
    const hasActiveProject = this.hasActiveProject;
    this.set('hasActiveProject', !hasActiveProject);
  }

  @action
  handleExtendedNetworkClicked() {
    const extendedNetwork = !this.extendedNetwork;
    // When true, cost and story code dropdowns are cleared and disabled
    if (extendedNetwork) {
      this.setProperties({
        storyCode: null,
        costCode: null,
        codesDisabled: true,
        extendedNetwork,
      });
    } else {
      this.setProperties({
        codesDisabled: false,
        extendedNetwork,
      });
    }
  }

  @action
  handleStoryCodeChange(storyCode) {
    this.set('storyCode', storyCode || null);
  }

  @action
  handleCostCodeChange(costCode) {
    this.set('costCode', costCode || null);
  }

  @action
  handleClearClicked() {
    if (!this.isClearButtonDisabled) {
      this.resetSearchParams();
    }
  }

  @action
  handleSubmitClicked() {
    const params = this.buildSearchParams();
    this.set('searchParams', params);
    this.loadSearchResultsPageTask.perform(this.companyId, this.searchParams, 1);
  }

  @action
  handleHireClicked(userIdOrSlug) {
    if (userIdOrSlug) {
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CREATE__DIRECT, userIdOrSlug);
    }
  }

  @action
  handleSidebarClose() {
    this.set('selectedFreelancer', null);
  }
}

export default MyNetworkController;
